import React, { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5295';

export default function InternalPoolPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ entraObjectId: '', entraTenantId: '', email: '', displayName: '', isPlatformAdmin: false });
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/v1/iam/internal-users`);
      setUsers(res.ok ? await res.json() : []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    setError(null);
    try {
      const res = await fetch(`${API}/api/v1/iam/internal-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Create failed: ${res.status}`);
      }
      setShowCreate(false);
      setForm({ entraObjectId: '', entraTenantId: '', email: '', displayName: '', isPlatformAdmin: false });
      await refresh();
    } catch (e) { setError(e.message); }
  };

  const togglePlatformAdmin = async (id, current) => {
    await fetch(`${API}/api/v1/iam/internal-users/${id}/platform-admin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isPlatformAdmin: !current }),
    });
    refresh();
  };

  const deactivate = async (id) => {
    if (!confirm('Deactivate this user? Existing assignments are preserved for audit but the user can no longer sign in.')) return;
    await fetch(`${API}/api/v1/iam/internal-users/${id}/deactivate`, { method: 'POST' });
    refresh();
  };

  return (
    <div style={{ maxWidth: 960, margin: '32px auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0D1B2A' }}>Internal user pool</h2>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ Add internal user</button>
      </div>
      <div style={{ fontSize: 11, color: '#7A8899', marginBottom: 20 }}>
        Kloudynet staff (Admin / Assessor / Presales / CSP). Platform admins are auto-assigned to every new tenant on activation.
      </div>

      {error && (
        <div style={{ padding: 10, marginBottom: 16, background: '#FFEBEE', color: '#C62828', borderRadius: 6, fontSize: 12 }}>
          {error}
        </div>
      )}

      {showCreate && (
        <div style={{ background: '#fff', border: '1px solid #E8ECF0', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#0D1B2A', marginBottom: 12 }}>Create internal user</div>
          {[
            { k: 'entraObjectId', label: 'Entra Object ID (oid)' },
            { k: 'entraTenantId', label: 'Entra Tenant ID (tid)' },
            { k: 'email', label: 'Email' },
            { k: 'displayName', label: 'Display name (optional)' },
          ].map(f => (
            <div key={f.k} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: '#7A8899', marginBottom: 4 }}>{f.label}</div>
              <input
                value={form[f.k]}
                onChange={e => setForm({ ...form, [f.k]: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', fontSize: 12, border: '1px solid #E0E4E8', borderRadius: 6 }} />
            </div>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, marginBottom: 14 }}>
            <input type="checkbox"
              checked={form.isPlatformAdmin}
              onChange={e => setForm({ ...form, isPlatformAdmin: e.target.checked })} />
            Platform admin (auto-assigned to every new tenant)
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="btn-primary" onClick={create}>Create</button>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #E8ECF0', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F8F9FA' }}>
              <th style={th}>Email</th>
              <th style={th}>Display</th>
              <th style={th}>Platform admin</th>
              <th style={th}>Status</th>
              <th style={{ ...th, textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#7A8899' }}>Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#7A8899' }}>No internal users yet.</td></tr>
            ) : users.map(u => (
              <tr key={u.id} style={{ borderTop: '1px solid #F0F2F5' }}>
                <td style={td}>{u.email}</td>
                <td style={td}>{u.displayName || '—'}</td>
                <td style={td}>
                  <button onClick={() => togglePlatformAdmin(u.id, u.isPlatformAdmin)}
                    style={{ fontSize: 10, padding: '3px 10px', borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: u.isPlatformAdmin ? '#E3F2FD' : '#F5F7F9',
                      color: u.isPlatformAdmin ? '#0078D4' : '#7A8899' }}>
                    {u.isPlatformAdmin ? '✓ Yes' : 'No'}
                  </button>
                </td>
                <td style={td}>{u.deactivatedAt ? <span style={{ color: '#C62828' }}>Deactivated</span> : 'Active'}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {!u.deactivatedAt && (
                    <button className="btn-secondary" style={{ fontSize: 10 }} onClick={() => deactivate(u.id)}>Deactivate</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = { padding: '8px 10px', textAlign: 'left', color: '#455A64', fontWeight: 600 };
const td = { padding: '8px 10px', color: '#0D1B2A' };
